/**
 * The navigation bar resolves twice — once per release as rows are added, and
 * once per person (piece 12, decision 0120). 🔨 An item the viewer cannot open
 * is hidden, not disabled: a locked door is not information, and the Invite New
 * User modal set the precedent. A group whose every item is filtered out
 * renders nothing at all, because a heading with silence under it reads as a
 * fault.
 */
import type { IconMeaning } from '../icons';
import { canReadModule, canUseCapability } from '../permissions/guards';
import { externalSession, portalNavRoute, portalNavRouteAllowed, portalSides } from '../permissions/portal';
import { isModule, type Module } from '../permissions/modules';
import type { GateContext } from '../permissions/guards';
import type { Viewer } from '../permissions/viewer';
import { ALL_MODULES_OFF, moduleRowVisible, type ModuleStates } from '../settings/modules';

export interface NavGroupRow {
  key: string;
  label: string;
  sortRank: number;
  isActive?: boolean;
}

export interface NavItemRow {
  key: string;
  label: string;
  route: string;
  /**
   * 🔨 The item's own glyph, as a meaning from the icon reference (piece 12).
   * The collapsed rail draws it alone, so every row carries one; a row read
   * from an older database with none falls back to the generic marker.
   */
  icon?: IconMeaning | null;
  /** Null pins the item above the groups; Dashboard is the only seeded row with none. */
  groupKey?: string | null;
  /** The Module the viewer passes canReadModule on; null renders for every viewer. */
  permissionKey?: string | null;
  /**
   * Piece 27, decision 0520: the optional module's Settings key the row belongs
   * to, and the bar hides the row while that module is off. 🔨 A different
   * question from `permissionKey`, which stays the access one.
   */
  moduleKey?: string | null;
  /**
   * 🔒 Decision 1467: a capability key the viewer must hold as well, answered by
   * `canUseCapability`. HR is a gate and not a Module (decision 0091), so the HR
   * group's row names no Module and carries `hr.employees.view` here. Null asks
   * nothing beyond `permissionKey`.
   */
  capabilityKey?: string | null;
  sortRank: number;
  isActive?: boolean;
}

export interface ResolvedNavGroup {
  key: string;
  label: string;
  items: readonly NavItemRow[];
}

export interface ResolvedNav {
  /** The ungated rows above the groups, Dashboard among them. */
  pinned: readonly NavItemRow[];
  groups: readonly ResolvedNavGroup[];
  /**
   * 🔒 Every section starts open rather than folded (decision 1441; T692):
   * set for an external session, whose bar is a handful of rows with its own
   * company among them, so folding would hide the only pages it has. Absent
   * for everybody else.
   */
  unfolded?: boolean;
}

export class NavigationError extends Error {}

/**
 * Dashboard is pinned and never gated, which is what a null group and a null
 * permission mean in data. 🔒 An external session is held to the portal's own
 * table of routes first (piece 8; decision 0607): a row absent from it is
 * hidden however the role's grid reads, which is why an ungated row — the
 * Calendar — does not reach a customer contact's bar.
 */
export function itemIsVisible(item: NavItemRow, viewer: Viewer, ctx: GateContext, opened: readonly string[] = []): boolean {
  if (item.isActive === false) return false;
  // 🔒 A row the server opens by a rule no Module carries (decision 2320; T1391) is shown to the person it names, beside the portal's table.
  if (opened.includes(item.key) && !(externalSession(viewer, ctx) && !portalNavRouteAllowed(item.route, portalSides(viewer)))) return true;
  // 🔒 An outside login's bar carries the portal's own rows, and a document row only on a side its Company is tagged with (decisions 0607, 0689).
  if (externalSession(viewer, ctx) && !portalNavRouteAllowed(item.route, portalSides(viewer))) return false;
  // 🔒 Decision 1467: a capability the row names is asked first, and a row naming one and no Module is gated on it alone.
  if (item.capabilityKey && !canUseCapability(viewer, item.capabilityKey, ctx)) return false;
  const key = item.permissionKey;
  if (key === null || key === undefined) return true;
  if (!isModule(key)) throw new NavigationError(`a NavItem names a Module, and '${key}' is not one of the twelve`);
  return canReadModule(viewer, key as Module, ctx);
}

const bySortRank = <T extends { sortRank: number; key: string }>(a: T, b: T): number => a.sortRank - b.sortRank || a.key.localeCompare(b.key);

/**
 * The bar for one viewer. `sortRank` keeps the order stable as items drop out,
 * so two people with different access see the same arrangement of what they
 * both see.
 */
export function resolveNav(groups: readonly NavGroupRow[], items: readonly NavItemRow[], viewer: Viewer, ctx: GateContext, modules: ModuleStates = ALL_MODULES_OFF, opened: readonly string[] = []): ResolvedNav {
  // 🔨 Piece 27, decision 0520: a disabled module's rows are hidden here, beside the access filter and not instead of
  // it. Hiding is presentation — somebody who types the route still reaches it, and the route's own guard refuses them.
  const external = externalSession(viewer, ctx);
  const visible = items
    .filter((item) => moduleRowVisible(item, modules) && itemIsVisible(item, viewer, ctx, opened))
    .map((item) => (external ? { ...item, route: portalNavRoute(item.route, viewer.companyId) } : item))
    .sort(bySortRank);
  const pinned = visible.filter((item) => !item.groupKey);
  const resolved: ResolvedNavGroup[] = [];
  for (const group of [...groups].filter((g) => g.isActive !== false).sort(bySortRank)) {
    const own = visible.filter((item) => item.groupKey === group.key);
    // 🔨 No header and no empty container: a group with nothing under it renders nothing.
    if (own.length > 0) resolved.push({ key: group.key, label: group.label, items: own });
  }
  // 🔒 An outside login's bar starts with every section open, its own company's row among them (decision 1441).
  return external ? { pinned, groups: resolved, unfolded: true } : { pinned, groups: resolved };
}

/** Every route the registry knows, which the route-guard sweep of the auth piece reads. */
export function registeredRoutes(items: readonly NavItemRow[]): string[] {
  return items.filter((i) => i.isActive !== false).map((i) => i.route);
}

/**
 * 🔨 Hiding a row is presentation, not access control: somebody who types the
 * route still reaches it, so a route with a nav row and no guard is a hole.
 */
export const HIDING_IS_NOT_ACCESS_CONTROL = true;

// ---- The two rendering modes of the desktop bar ----

/**
 * 🔨 Collapsing is a distinct rendering mode, not a CSS width transition on the
 * same markup: the collapsed rail drops the group structure entirely and renders
 * icon-only rows. 💰 Read as a width transition, a builder animates the same
 * markup and shrinks group headers to unreadable stubs.
 */
export type NavRendering = 'expanded' | 'rail';

export function navRendering(collapsed: boolean): NavRendering {
  return collapsed ? 'rail' : 'expanded';
}

/**
 * The section holding the page being read, or null where no section does
 * (decision 1441; T692). A row's route matches its own path and every path
 * beneath it; a query on the row is ignored here, because both Payments rows
 * sit in one section.
 */
export function currentNavGroup(nav: ResolvedNav, pathname: string): string | null {
  for (const group of nav.groups) {
    for (const item of group.items) {
      const path = item.route.split('?')[0] ?? '';
      if (path === '/') continue;
      if (pathname === path || pathname.startsWith(`${path}/`)) return group.key;
    }
  }
  return null;
}

/**
 * 🔒 Whether a section of the bar is open (decision 1441; T692). The person's
 * own choice wins where they made one; otherwise the section holding the page
 * being read is open and every other starts folded, which is what a fresh
 * sign-in meets. An unfolded bar, an external session's, starts with every
 * section open, because it lands on no section's page and its own company's
 * row would otherwise be folded out of sight.
 */
export function navGroupIsOpen(groupKey: string, chosen: Readonly<Record<string, boolean>>, current: string | null, unfolded = false): boolean {
  const own = chosen[groupKey];
  return own ?? (unfolded || groupKey === current);
}

/** In the rail every visible item is one icon row, groups flattened away, order preserved. */
export function railRows(nav: ResolvedNav): readonly NavItemRow[] {
  return [...nav.pinned, ...nav.groups.flatMap((g) => g.items)];
}
